import type { QueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { EXPECTED_WORKSPACE_HEADER, WORKSPACE_RESPONSE_HEADER, WORKSPACE_CHANGED_CODE, isWorkspaceWrite } from "@shared/workspaceContract";
import { browserCalendarTimeZone, CALENDAR_TIME_ZONE_HEADER } from "@shared/calendarTimeZone";

const ISOLATION_VERSION_KEY = "incontrol.tenant-isolation-version";
const ISOLATION_VERSION = "1";
const LAST_IDENTITY_KEY = "incontrol.last-auth-identity";
const LAST_WORKSPACE_KEY = "incontrol.last-effective-workspace";
const SIGNED_OUT_IDENTITY = "__signed_out__";
const WORKSPACE_NOTICE_KEY = "incontrol.workspace-change-notice";
const WORKSPACE_NOTICE = "The account changed. Any rejected changes were not saved. The editor has reloaded to keep accounts separate.";
const SENSITIVE_LOCAL_STORAGE_KEYS = new Set([
  "incontrol.addMediaDraftIds",
  "incontrol.pendingAvatarVideoNames",
]);

function clearSensitiveLocalStorage(): void {
  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key && (key.startsWith("draft:") || SENSITIVE_LOCAL_STORAGE_KEYS.has(key))) {
      window.localStorage.removeItem(key);
    }
  }
}

export function isolateBrowserForIdentity(
  queryClient: QueryClient,
  userId: string | null,
): void {
  if (typeof window === "undefined") return;
  const identity = userId || SIGNED_OUT_IDENTITY;
  const priorIdentity = window.sessionStorage.getItem(LAST_IDENTITY_KEY);
  const version = window.sessionStorage.getItem(ISOLATION_VERSION_KEY);
  const identityChanged = priorIdentity !== null && priorIdentity !== identity;
  const firstHardenedSession = version !== ISOLATION_VERSION;

  if (identityChanged || firstHardenedSession) {
    void queryClient.cancelQueries();
    queryClient.getMutationCache().clear();
    queryClient.clear();
    clearSensitiveLocalStorage();
    window.sessionStorage.removeItem(LAST_WORKSPACE_KEY);
  }

  window.sessionStorage.setItem(LAST_IDENTITY_KEY, identity);
  window.sessionStorage.setItem(ISOLATION_VERSION_KEY, ISOLATION_VERSION);
}

// Capture before asynchronous token retrieval so a pending save never picks up
// the next workspace's identity while keeping the previous editor's payload.
export function expectedWorkspaceHeaders(url: string): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (!isSameOriginApi(url)) return {};
  const workspace = window.sessionStorage.getItem(LAST_WORKSPACE_KEY);
  return workspace ? { [EXPECTED_WORKSPACE_HEADER]: workspace } : {};
}

function isSameOriginApi(input: string): boolean {
  const url = new URL(input, window.location.href);
  return url.origin === window.location.origin && url.pathname.startsWith("/api/");
}

export function installEffectiveWorkspaceIsolation(
  queryClient: QueryClient,
  reload: () => void = () => window.location.reload(),
): () => void {
  if (typeof window === "undefined") return () => {};
  const originalFetch = window.fetch.bind(window);
  // Pin this document, not a mutable storage value shared with later responses.
  let editorWorkspace = window.sessionStorage.getItem(LAST_WORKSPACE_KEY);
  let editorIdentity = window.sessionStorage.getItem(LAST_IDENTITY_KEY);
  let changingWorkspace = false;
  if (window.sessionStorage.getItem(WORKSPACE_NOTICE_KEY)) {
    window.sessionStorage.removeItem(WORKSPACE_NOTICE_KEY);
    toast({ title: "Account changed", description: WORKSPACE_NOTICE });
  }

  const abortForWorkspaceChange = (): never => {
    if (!changingWorkspace) {
      changingWorkspace = true;
      window.sessionStorage.setItem(WORKSPACE_NOTICE_KEY, "1");
      reload();
    }
    throw new DOMException("Effective workspace changed", "AbortError");
  };

  const isolatedFetch: typeof window.fetch = async (input, init) => {
    if (!isSameOriginApi(input instanceof Request ? input.url : String(input))) {
      return originalFetch(input, init);
    }
    if (changingWorkspace) return abortForWorkspaceChange();
    const requestIdentity = window.sessionStorage.getItem(LAST_IDENTITY_KEY);
    if (editorIdentity !== requestIdentity) {
      // The identity boundary has already discarded and remounted old editors.
      editorIdentity = requestIdentity;
      editorWorkspace = window.sessionStorage.getItem(LAST_WORKSPACE_KEY);
    }
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    const timeZone = browserCalendarTimeZone();
    if (timeZone) headers.set(CALENDAR_TIME_ZONE_HEADER, timeZone);
    if (isWorkspaceWrite(method)) {
      if (editorWorkspace && !headers.has(EXPECTED_WORKSPACE_HEADER)) {
        headers.set(EXPECTED_WORKSPACE_HEADER, editorWorkspace);
      }
    }
    const guardedInit = { ...init, headers };
    const response = await originalFetch(input, guardedInit);
    if (requestIdentity !== window.sessionStorage.getItem(LAST_IDENTITY_KEY)) {
      throw new DOMException("Signed-in identity changed", "AbortError");
    }
    if (changingWorkspace) return abortForWorkspaceChange();
    const workspaceId = response.headers.get(WORKSPACE_RESPONSE_HEADER)?.trim();
    // Also handle an explicit mismatch even if the response header is absent.
    const mismatch = response.status === 409 &&
      (await response.clone().json().catch(() => null))?.code === WORKSPACE_CHANGED_CODE;
    if (mismatch) {
      if (workspaceId) isolateBrowserForWorkspace(queryClient, workspaceId);
      else {
        void queryClient.cancelQueries();
        queryClient.clear();
        clearSensitiveLocalStorage();
      }
      return abortForWorkspaceChange();
    }
    if (!workspaceId) return response;
    if (isolateBrowserForWorkspace(queryClient, workspaceId)) {
      return abortForWorkspaceChange();
    }
    editorWorkspace ??= workspaceId;
    return response;
  };

  window.fetch = isolatedFetch;
  return () => {
    if (window.fetch === isolatedFetch) window.fetch = originalFetch;
  };
}

export function isolateBrowserForWorkspace(
  queryClient: QueryClient,
  workspaceId: string,
): boolean {
  if (typeof window === "undefined" || !workspaceId.trim()) return false;
  const normalized = workspaceId.trim();
  const priorWorkspaceId = window.sessionStorage.getItem(LAST_WORKSPACE_KEY);
  window.sessionStorage.setItem(LAST_WORKSPACE_KEY, normalized);
  if (!priorWorkspaceId || priorWorkspaceId === normalized) return false;

  void queryClient.cancelQueries();
  queryClient.getMutationCache().clear();
  queryClient.clear();
  clearSensitiveLocalStorage();
  return true;
}